import { CssBaseline, ThemeProvider, useTheme } from '@material-ui/core'
import { useAppRunningContext } from 'features/EmbeddedRh24/useAppRunningContext'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import ReduxToastr from 'react-redux-toastr'
import { SetDefaultTheme } from 'store/Application/appActions'
import { useAppDispatch, useAppSelector } from 'store/configureStore'
import { BuiltInThemes, createAppTheme } from 'utils/appTheme'

import 'react-redux-toastr/lib/css/react-redux-toastr.min.css'

const ToastrStyles = () => {
  const theme = useTheme()

  return (
    <style>
      {`
        .qf-toastr {
          background-color: ${theme.palette.background.paper} !important;
          box-shadow: ${theme.shadows[1]};
          opacity: 1 !important;
        }
        .qf-toastr .rrt-holder {
          margin-top: unset !important;
          transform: translateY(-50%);
          height: unset !important;
          line-height: unset !important;
        }
        .qf-toastr .rrt-middle-container {
          font-family: ${theme.typography.fontFamily} !important;
        }
        .qf-toastr-error {
          background-color: ${theme.palette.error.main} !important;
          color: ${theme.palette.error.contrastText} !important;
        }
        .qf-tastr-error .rr-progress-container {
          background-color: ${theme.palette.error.dark}
        }
        .qf-toastr-success {
          background-color: ${theme.palette.success.main} !important;
          color: ${theme.palette.success.contrastText} !important;
        }
        .qf-tastr-success .rr-progress-container {
          background-color: ${theme.palette.success.dark}
        }
        .qf-toastr-info {
          background-color: ${theme.palette.info.main} !important;
          color: ${theme.palette.info.contrastText} !important;
        }
        .qf-tastr-info .rr-progress-container {
          background-color: ${theme.palette.info.dark}
        }
        .qf-toastr-warning {
          background-color: ${theme.palette.warning.main} !important;
          color: ${theme.palette.warning.contrastText} !important;
        }
        .qf-tastr-warning .rr-progress-container {
          background-color: ${theme.palette.warning.dark}
        }
      `}
    </style>
  )
}

const FontImporter = () => {
  const theme = useTheme()
  const shouldRun = useRef(true)

  useEffect(() => {
    if (shouldRun.current) {
      shouldRun.current = false
    }
  }, [])

  if (!shouldRun) {
    return null
  }

  return (
    <>
      {theme.googleFonts?.split(',').map((fontFamily) => (
        <style key={fontFamily}>
          {`@import url("https://fonts.googleapis.com/css2?family=${fontFamily.trim()}&display=swap")`}
        </style>
      ))}
    </>
  )
}

const IconsCSSColorsVariables = () => {
  const theme = useTheme()

  return (
    <style>
      {`:root {
          --icon-outline: ${theme.iconColors.outline};
          --icon-white: ${theme.iconColors.white};
          --icon-highlight: ${theme.iconColors.highlight};
        }`}
    </style>
  )
}

const CSSColorScheme = () => {
  const theme = useTheme()

  return (
    <style>
      {`body {
          color-scheme: ${theme.palette.type};`}
    </style>
  )
}

const Layout = ({ children }) => {
  const theme = useAppSelector((state) => state.app.theme)
  const dispatch = useAppDispatch()
  const { withinIframe } = useAppRunningContext()

  useEffect(() => {
    if (!theme && !withinIframe) {
      dispatch(
        SetDefaultTheme(
          (localStorage.getItem('rh24BuiltInTheme') as BuiltInThemes) || 'light'
        )
      )
    }
  }, [dispatch, theme, withinIframe])

  return (
    <ThemeProvider theme={createAppTheme(theme)}>
      <CssBaseline />
      <FontImporter />
      <IconsCSSColorsVariables />
      <CSSColorScheme />
      <ToastrStyles />
      {children}
      <div tabIndex={-1} style={{ backgroundColor: '#e5e5e5' }}>
        <ReduxToastr
          preventDuplicates={true}
          position="top-left"
          className="qf-toastr"
        />
      </div>
    </ThemeProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
